import React, { useEffect, useRef, useState } from "react";
import "./complete-profile.css";
import { Button } from "../../components/primitives";
import { DropdownField, SingleRangeSlider } from "../../components/forms";
import Navigation from "../../components/navigation/Navigation";
import { US_STATES } from "../../domain/profile-options/states";
import { POLITICAL_AFFILIATION } from "../../domain/profile-options/political-affiliation";
import { RELIGIOUS_AFFILIATION } from "../../domain/profile-options/religious-affiliation";
import { GENDERS, MARITAL_STATUSES, SEXUAL_ORIENTATIONS } from "../../domain/profile-options/demographics";
import {
  COMPLETE_PROFILE_ETHNICITY_OPTIONS,
  COMPLETE_PROFILE_STEPS,
  useCompleteProfileForm,
} from "./complete-profile.functions";
import {
  PROFILE_EDITOR_PREVIEW_SIZE,
  clampImageOffsets,
  computeRenderedImageSize,
} from "../../components/profile-image-editor/profile-image-editor.functions";

function CompleteProfile() {
  const PREVIEW_SIZE = PROFILE_EDITOR_PREVIEW_SIZE;
  const fileInputRef = useRef(null);
  const dragRef = useRef(null);
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const [savedImageLoadFailed, setSavedImageLoadFailed] = useState(false);

  const {
    loading,
    uploading,
    error,
    currentStep,
    formData,
    profilePictureUrl,
    hasPendingImage,
    pendingImageUrl,
    pendingImageDimensions,
    imageZoom,
    imageOffsetX,
    imageOffsetY,
    profileNameModeration,
    storyModeration,
    sociopoliticalFilters,
    sociopoliticalQuestions,
    sociopoliticalPublic,
    setSociopoliticalPublic,
    interestOptions,
    handleChange,
    handleInterestToggle,
    handleSociopoliticalChange,
    handleIrrelevantToggle,
    handleImageUpload,
    setImageZoom,
    setImageOffsetX,
    setImageOffsetY,
    handleApplyImageAdjustments,
    handleCancelImageAdjustments,
    handleNextStep,
    handlePreviousStep,
    handleStepSelect,
    handleSubmit,
  } = useCompleteProfileForm();

  const hasEditablePreview = hasPendingImage && Boolean(pendingImageUrl);
  const showSavedImage = Boolean(profilePictureUrl) && !savedImageLoadFailed;
  const renderedSize = computeRenderedImageSize(pendingImageDimensions, imageZoom);
  const activeStep =
    COMPLETE_PROFILE_STEPS.find((step) => step.id === currentStep) ||
    COMPLETE_PROFILE_STEPS[0];
  const completionPercent = (currentStep / COMPLETE_PROFILE_STEPS.length) * 100;
  const stateOptions = US_STATES.filter((option) => option.value !== "ANY");
  const religiousAffiliationOptions = RELIGIOUS_AFFILIATION.filter(
    (option) => option.value !== "ANY"
  );
  const politicalAffiliationOptions = POLITICAL_AFFILIATION.filter(
    (option) =>
      option.value !== "ANY" &&
      !["Liberal", "Moderate", "Conservative", "Progressive"].includes(option.value)
  );

  const answeredViews = sociopoliticalQuestions.reduce((count, question) => {
    const entry = sociopoliticalFilters?.[question.id];
    return entry?.irrelevant ? count : count + 1;
  }, 0);

  const profileInitial = (formData?.profileName?.trim()?.[0] || "?").toUpperCase();
  const profileImageForSidebar = hasEditablePreview
    ? pendingImageUrl
    : showSavedImage
      ? profilePictureUrl
      : "";


  useEffect(() => {
    setSavedImageLoadFailed(false);
  }, [profilePictureUrl]);

  const normalizeOffsets = (nextX, nextY, zoom = imageZoom) =>
    clampImageOffsets(pendingImageDimensions, nextX, nextY, zoom);

  const handleZoomChange = (nextZoom) => {
    setImageZoom(nextZoom);
    const { x, y } = normalizeOffsets(imageOffsetX, imageOffsetY, nextZoom);
    setImageOffsetX(x);
    setImageOffsetY(y);
  };

  const handleImagePointerDown = (event) => {
    if (!hasEditablePreview) return;
    setIsDraggingImage(false);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      baseX: imageOffsetX,
      baseY: imageOffsetY,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleImagePointerMove = (event) => {
    if (!dragRef.current || !hasEditablePreview) return;
    const deltaX = event.clientX - dragRef.current.startX;
    const deltaY = event.clientY - dragRef.current.startY;

    if (!isDraggingImage && (Math.abs(deltaX) > 0 || Math.abs(deltaY) > 0)) {
      setIsDraggingImage(true);
    }

    const { x, y } = normalizeOffsets(
      dragRef.current.baseX - deltaX,
      dragRef.current.baseY - deltaY
    );
    setImageOffsetX(x);
    setImageOffsetY(y);
  };

  const handleImagePointerEnd = (event) => {
    if (!dragRef.current) return;
    if (event.currentTarget.hasPointerCapture(dragRef.current.pointerId)) {
      event.currentTarget.releasePointerCapture(dragRef.current.pointerId);
    }
    setIsDraggingImage(false);
    dragRef.current = null;
  };



  return (
    <>
      <Navigation isAuthenticated={false} />
      <div id="nav-scroll-sentinel" className="appScrollSentinel" />

      <div className="cp-page">
        <div className="cp-shell">
          <aside className="cp-sidebar">

            {/* Profile preview */}
            <section className="cp-profilePanel">
              {profileImageForSidebar ? (
                <img src={profileImageForSidebar} alt="Profile preview" className="cp-profileAvatarImage" />
              ) : (
                <div className="cp-profileAvatarFallback">{profileInitial}</div>
              )}
              <div className="cp-profileMeta">
                <div className="cp-profileName">{formData.profileName || "Your profile"}</div>
                <div className="cp-profileLocation">
                  {formData.city ? `${formData.city}${formData.state ? `, ${formData.state}` : ""}` : "Location pending"}
                </div>
              </div>
            </section>

            {/* Step progress */}
            <section className="cp-stepRail" aria-label="Profile setup progress">
              {COMPLETE_PROFILE_STEPS.map((step) => {
                const isActive = currentStep === step.id;
                const isComplete = currentStep > step.id;
                const isClickable = step.id <= currentStep;
                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={() => handleStepSelect(step.id)}
                    disabled={!isClickable}
                    className={[
                      "cp-stepRailItem",
                      isActive ? "cp-stepRailItem--active" : "",
                      isComplete ? "cp-stepRailItem--complete" : "",
                      isClickable ? "cp-stepRailItem--clickable" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <span className="cp-stepRailIndex">{step.id}</span>
                    <div className="cp-stepRailBody">
                      <div className="cp-stepRailLabel">{step.label}</div>
                      <div className="cp-stepRailState">
                        {isComplete ? "Completed" : isActive ? "In progress" : "Upcoming"}
                      </div>
                    </div>
                  </button>
                );
              })}

              <div className="cp-stepRailProgress" aria-hidden="true">
                <div className="cp-stepRailProgressTrack">
                  <div className="cp-stepRailProgressFill" style={{ width: `${completionPercent}%` }} />
                </div>
                <span className="cp-stepRailProgressLabel">{Math.round(completionPercent)}% complete</span>
              </div>
            </section>
          </aside>

          <main className="cp-main">
            {/* Step header */}
            <header className="cp-mainHeader">
              <div className="cp-mainHeaderTopRow">
                <div className="cp-mainTitleRow">
                  <h2 className="cp-mainTitle">{activeStep.title}</h2>
                  <span className="cp-stepCounter">Step {currentStep} of {COMPLETE_PROFILE_STEPS.length}</span>
                </div>
                <div className="cp-mainHeaderDots" aria-hidden="true">
                  <span className="cp-mainHeaderDot cp-mainHeaderDot--blue" />
                  <span className="cp-mainHeaderDot cp-mainHeaderDot--purple" />
                  <span className="cp-mainHeaderDot cp-mainHeaderDot--magenta" />
                </div>
              </div>
              {activeStep.description ? (
                <p className="cp-mainDescription">{activeStep.description}</p>
              ) : null}
            </header>

            {/* Persistent privacy callout — shown on every step of the wizard */}
            <div className="cp-privacyCallout" role="note">
              <span className="cp-privacyCalloutTitle">Your profile is anonymous</span>
              <span className="cp-privacyCalloutBody">
                Your name and email stay private. Other members only see the alias
                and details you choose to share.
              </span>
            </div>

            {/* Error state */}
            {error && (
              <div className="cp-alert cp-alert--error" role="alert">
                <span className="cp-alertIcon" aria-hidden="true">!</span>
                <span>{error}</span>
              </div>
            )}

            <form className="cp-formSurface" onSubmit={currentStep === 4 ? handleSubmit : handleNextStep}>
            {/* Step 1: basic info */}
            {currentStep === 1 && (
              <>
                <section className="cp-sectionCard">
                  <h3 className="cp-sectionTitle">Basic Information</h3>
                  <div className="cp-grid cp-grid--2">
                    <div className="cp-field">
                      <label className="cp-label">
                        Profile Name <span className="input-required">*</span>
                      </label>
                      <input
                        type="text"
                        name="profileName"
                        value={formData.profileName}
                        onChange={handleChange}
                        required
                        aria-invalid={profileNameModeration.blocked || undefined}
                        aria-describedby={profileNameModeration.blocked ? "profileName-error" : undefined}
                        className="cp-input"
                      />
                      {profileNameModeration.blocked ? (
                        <small
                          id="profileName-error"
                          className="cp-help cp-help--warning"
                          role="alert"
                        >
                          {profileNameModeration.feedback}
                        </small>
                      ) : null}
                    </div>
                  </div>
                </section>

                <section className="cp-sectionCard">
                  <h3 className="cp-sectionTitle">Location</h3>
                  <div className="cp-grid cp-grid--3">
                    <div className="cp-field">
                      <label className="cp-label">
                        Country <span className="input-required">*</span>
                      </label>
                      <input
                        type="text"
                        name="country"
                        value="United States"
                        onChange={handleChange}
                        readOnly
                        className="cp-input"
                      />
                    </div>

                    <div className="cp-field cp-field--dropdown">
                      <DropdownField
                        label="State/Region"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        options={stateOptions}
                        placeholder="Select State"
                        required
                      />
                    </div>

                    <div className="cp-field">
                      <label className="cp-label">
                        City <span className="input-required">*</span>
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        placeholder="Enter city name"
                        className="cp-input"
                      />
                    </div>
                  </div>
                </section>

              </>
            )}

            {/* Step 2: about you */}
            {currentStep === 2 && (
              <>

                <section className="cp-sectionCard">
                  <h3 className="cp-sectionTitle">Community Context</h3>
                  <div className="cp-grid cp-grid--2">
                    <div className="cp-field">
                      <label className="cp-label">
                        Date of Birth <span className="input-required">*</span>
                      </label>
                      <input
                        type="date"
                        name="dateOfBirth"
                        value={formData.dateOfBirth}
                        onChange={handleChange}
                        required
                        className="cp-input"
                      />
                    </div>

                    <div className="cp-field cp-field--dropdown">
                      <DropdownField
                        label="Ethnicity"
                        name="ethnicity"
                        value={formData.ethnicity}
                        onChange={handleChange}
                        options={COMPLETE_PROFILE_ETHNICITY_OPTIONS}
                        placeholder="Select Ethnicity"
                        required
                      />
                    </div>

                    <div className="cp-field cp-field--dropdown">
                      <DropdownField
                        label="Marital Status"
                        name="maritalStatus"
                        value={formData.maritalStatus}
                        onChange={handleChange}
                        options={MARITAL_STATUSES}
                        placeholder="Select Marital Status"
                        required
                      />
                    </div>

                    <div className="cp-field cp-field--dropdown">
                      <DropdownField
                        label="Gender"
                        name="gender"
                        value={formData.gender}
                        onChange={handleChange}
                        options={GENDERS}
                        placeholder="Select Gender"
                        required
                      />
                    </div>
                    <div className="cp-field cp-field--dropdown">
                      <DropdownField
                        label="Sexual Orientation"
                        name="sexualOrientation"
                        value={formData.sexualOrientation}
                        onChange={handleChange}
                        options={SEXUAL_ORIENTATIONS}
                        placeholder="Select Sexual Orientation"
                        required
                      />
                    </div>

                    <div className="cp-field">
                      <label className="cp-label">Occupation</label>
                      <input
                        type="text"
                        name="occupation"
                        value={formData.occupation}
                        onChange={handleChange}
                        className="cp-input"
                      />
                    </div>

                    <div className="cp-field cp-field--dropdown">
                      <DropdownField
                        label="Religious Affiliation"
                        name="religiousAffiliation"
                        value={formData.religiousAffiliation}
                        onChange={handleChange}
                        options={religiousAffiliationOptions}
                        placeholder="Select Religious Affiliation"
                        required
                      />
                    </div>

                    <div className="cp-field cp-field--dropdown">
                      <DropdownField
                        label="Political Affiliation"
                        name="politicalAffiliation"
                        value={formData.politicalAffiliation}
                        onChange={handleChange}
                        options={politicalAffiliationOptions}
                        placeholder="Select Political Affiliation"
                        required
                      />
                    </div>
                  </div>
                </section>

                <section className="cp-sectionCard">
                  <h3 className="cp-sectionTitle">About You</h3>
                  <div className="cp-field">
                    <label className="cp-label">
                      Your Story (in a few words) <span className="input-required">*</span>
                    </label>
                    <textarea
                      name="story"
                      value={formData.story}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={4}
                      placeholder="Tell us a bit about yourself..."
                      aria-invalid={storyModeration.blocked || undefined}
                      aria-describedby={storyModeration.blocked ? "story-error" : undefined}
                      className="cp-textarea"
                    />
                    <small className="cp-help">{formData.story.length}/500 characters</small>
                    {storyModeration.blocked ? (
                      <small
                        id="story-error"
                        className="cp-help cp-help--warning"
                        role="alert"
                      >
                        {storyModeration.feedback}
                      </small>
                    ) : null}
                  </div>
                </section>

                <section className="cp-sectionCard">
                  <h3 className="cp-sectionTitle">Interests</h3>
                  <p className="cp-sectionHint">Choose at least one.</p>
                  <div className="cp-interestGrid">
                    {interestOptions.map((interest) => (
                      <Button
                        key={interest}
                        type="button"
                        variant={formData.interests.includes(interest) ? "primary" : "outline"}
                        size="sm"
                        onClick={() => handleInterestToggle(interest)}
                        className="cp-interestPill"
                      >
                        {interest}
                      </Button>
                    ))}
                  </div>
                </section>
              </>
            )}


            {/* Step 3: sociopolitical views */}
            {currentStep === 3 && (
              <section className="cp-sectionCard">
                <div className="cp-viewsHeader">
                  <h3 className="cp-sectionTitle">Sociopolitical Views</h3>
                  <span className="cp-viewsCount">Answered {answeredViews}/{sociopoliticalQuestions.length}</span>
                </div>
                <div className="cp-sliderStack">
                  {sociopoliticalQuestions.map((question, index) => (
                    <div className="cp-sliderCard" key={question.id}>
                      <span className="cp-sliderIndex" aria-hidden="true">{index + 1}</span>
                      <div className="cp-sliderInner">
                        <SingleRangeSlider
                          label={question.label}
                          leftLabel={question.leftLabel}
                          rightLabel={question.rightLabel}
                          value={sociopoliticalFilters[question.id].value || 4}
                          onChange={(val) => handleSociopoliticalChange(question.id, val)}
                          min={1}
                          max={7}
                          irrelevant={sociopoliticalFilters[question.id].irrelevant}
                          onIrrelevantToggle={() => handleIrrelevantToggle(question.id)}
                          showOptOut={true}
                          showTicks={true}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="cp-visibilityRow">
                  <label
                    className="cp-visibilityToggle"
                    htmlFor="cp-sociopoliticalVisibility"
                  >
                    <span className="cp-visibilityText">
                      <span className="cp-visibilityLabel">
                        Show my answers on my profile
                      </span>
                      <span className="cp-visibilityHint">
                        {sociopoliticalPublic
                          ? "Public — other members can see your answers."
                          : "Private — others see “This member’s answers are private.”"}{" "}
                        You can change this anytime in Settings.
                      </span>
                    </span>
                    <input
                      id="cp-sociopoliticalVisibility"
                      className="cp-visibilityInput"
                      type="checkbox"
                      role="switch"
                      aria-checked={sociopoliticalPublic}
                      checked={sociopoliticalPublic}
                      onChange={() => setSociopoliticalPublic((prev) => !prev)}
                    />
                    <span className="cp-visibilityTrack" aria-hidden="true">
                      <span className="cp-visibilityThumb" />
                    </span>
                  </label>
                </div>
              </section>
            )}

            {/* Step 4: profile photo */}
            {currentStep === 4 && (
              <>
              <section className="cp-sectionCard">
                <h3 className="cp-sectionTitle">Profile Photo (Optional)</h3>
                <div className="cp-photoLayout">
                  <div className="cp-photoPreviewPanel">
                    <div className="cp-photo-wrap">
                      {hasEditablePreview ? (
                        <div
                          className="cp-profile-picture-editor"
                          onPointerDown={handleImagePointerDown}
                          onPointerMove={handleImagePointerMove}
                          onPointerUp={handleImagePointerEnd}
                          onPointerCancel={handleImagePointerEnd}
                          style={{
                            "--cp-image-offset-x": `${imageOffsetX}px`,
                            "--cp-image-offset-y": `${imageOffsetY}px`,
                            "--cp-crop-size": `${PREVIEW_SIZE}px`,
                            cursor: isDraggingImage ? "grabbing" : "default",
                          }}
                        >
                          <img
                            className="cp-profile-picture-editor-image"
                            src={pendingImageUrl}
                            alt="Profile preview"
                            draggable={false}
                            style={{
                              width: `${renderedSize.width}px`,
                              height: `${renderedSize.height}px`,
                            }}
                          />
                          <div className="cp-profile-picture-crop-guide" />
                        </div>
                      ) : showSavedImage ? (
                        <img
                          className="cp-profile-picture"
                          src={profilePictureUrl}
                          alt="Profile"
                          onError={() => setSavedImageLoadFailed(true)}
                        />
                      ) : (
                        <div className="cp-profile-picture-fallback">{profileInitial}</div>
                      )}
                    </div>
                  </div>

                  <div className="cp-photoControlPanel">
                    <label className="cp-label">Profile Picture (Optional)</label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: "none" }}
                    />

                    {!hasPendingImage && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="cp-upload-button"
                      >
                        Choose Photo
                      </Button>
                    )}

                    {hasPendingImage && (
                      <div className="cp-image-controls">
                        <label className="cp-control-label">Zoom</label>
                        <input
                          className="cp-control-range"
                          type="range"
                          min="1"
                          max="2.5"
                          step="0.01"
                          value={imageZoom}
                          onChange={(e) => handleZoomChange(Number(e.target.value))}
                        />

                        <div className="cp-control-instruction">
                          Drag within the preview frame to position your photo.
                        </div>

                        <div className="cp-image-control-actions">
                          <Button
                            type="button"
                            variant="primary"
                            onClick={handleApplyImageAdjustments}
                            disabled={uploading}
                          >
                            {uploading ? "Uploading..." : "Apply Photo"}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleCancelImageAdjustments}
                            disabled={uploading}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}

                    <small className="cp-help">Optional — you can add or update this later. Max 5MB, JPG/PNG</small>
                  </div>
                </div>
              </section>
              </>
            )}

              <div className={`cp-actions ${currentStep === 1 ? "cp-actions--single" : ""}`}>
                {currentStep > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    onClick={handlePreviousStep}
                    className="cp-actionButton"
                  >
                    Previous
                  </Button>
                )}

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={
                    loading ||
                    uploading ||
                    (currentStep === 2 && formData.interests.length === 0) ||
                    (currentStep === 1 && profileNameModeration.blocked) ||
                    (currentStep === 3 && storyModeration.blocked)
                  }
                  aria-busy={loading || uploading}
                  className="cp-actionButton"
                >
                  {loading ? "Saving..." : uploading ? "Uploading..." : currentStep === 4 ? "Save Profile" : "Next Step"}
                </Button>
              </div>
            </form>
          </main>
        </div>
      </div>
    </>
  );
}

export default CompleteProfile;
